// ============================================
// Tipos para el Sistema de Adopción
// ============================================

export type Id = number;

// ============================================
// Perfil del Adoptante
// ============================================

export interface CityInfo {
  id: Id;
  name?: string;
  department?: string;
  label?: string;
}

export interface ProfileUserInfo {
  id?: Id;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  full_name?: string;
  email?: string;
  phone?: string;
}

export interface AdopterProfile {
  id: Id;
  userId?: Id | null;
  shelterId?: Id | null;

  // Información de contacto
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  user?: ProfileUserInfo | null;

  // Información de vivienda
  city?: string | number | CityInfo | null;
  cityId?: Id | null;
  city_id?: Id | null;
  cityName?: string | null;
  cityLabel?: string | null;
  address?: string;
  housingType?: string;             // "Casa", "Apartamento", "Finca"
  housingOwnership?: string;        // "Propia", "Alquilada"

  // Composición familiar
  hasChildren?: boolean;
  childrenAges?: string;            // "5, 8, 12 años"
  hasElderly?: boolean;

  // Mascotas existentes
  hasOtherPets?: boolean;
  otherPetsDesc?: string;           // "2 perros, 1 gato"

  // Otros
  vehicleAvailable?: boolean;
  notes?: string;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

// ============================================
// Preferencias del Adoptante
// ============================================

export interface AdopterPreference {
  id: Id;
  adopterProfileId: Id;
  
  // Preferencias principales
  speciesId?: Id;
  sizeId?: Id;
  sexId?: Id;
  
  // Rango de edad
  minAgeMonths?: number;
  maxAgeMonths?: number;
  
  // Comportamiento
  wantsGoodWithAnimals?: boolean;
  
  // Preferencias opcionales
  preferredBreedId?: Id;
  preferredColorId?: Id;
  
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

// ============================================
// Formularios Dinámicos
// ============================================

export interface AdoptionFormTemplate {
  id: Id;
  shelterId?: Id | null;            // null = template global
  code: string;                     // "default_adoption_form"
  name: string;
  description?: string;
  version: number;
  isActive: boolean;
  schemaJson: AdoptionFormSchema;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdoptionFormSchema {
  title?: string;
  description?: string;
  version?: number;
  language?: string;
  sections: FormSection[];
}

export interface FormSection {
  id: string;
  label?: string;  // Backend usa 'label'
  title?: string;  // Mantido para compatibilidade
  description?: string;
  fields: FormField[];
}

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  validation?: FieldValidation;
  options?: SelectOption[];
  binding?: FieldBinding;
  conditional?: FieldConditional;
  visibleIf?: { field: string; equals: any };
  lookupType?: 'species' | 'size' | 'sex' | 'breed' | 'color' | 'status';
}

export type FieldType = 
  | 'text'
  | 'email'
  | 'tel'
  | 'number'
  | 'textarea'
  | 'select'
  | 'radio'
  | 'yesno'
  | 'checkbox'
  | 'date'
  | 'checkbox'
  | 'date'
  | 'range'
  | 'city'
  | 'lookup';

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;                 // Regex
  message?: string;
}

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface FieldBinding {
  table: 'adopter_profile' | 'adopter_preference';
  column?: string;
  columns?: {
    min?: string;
    max?: string;
  };
}

export interface FieldConditional {
  field: string;
  operator: '==' | '!=' | '>' | '<' | 'in' | 'contains';
  value: any;
}

// ============================================
// Respuesta de Formulario
// ============================================

export interface AdoptionFormResponse {
  id: Id;
  templateId: Id;
  adopterProfileId: Id;
  shelterId?: Id | null;
  animalId?: Id | null;
  answersJson: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================
// Solicitud de Adopción
// ============================================

export interface AdoptionApplication {
  id: Id;
  adopterProfileId: Id;
  animalId: Id;
  shelterId?: Id | null;
  status: ApplicationStatus;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type ApplicationStatus = 
  | 'SUBMITTED'      // Enviada
  | 'IN_REVIEW'      // En revisión
  | 'APPROVED'       // Aprobada
  | 'REJECTED'       // Rechazada
  | 'CANCELLED';     // Cancelada

export interface AdoptionApplicationTimeline {
  id: Id;
  applicationId: Id;
  eventType: TimelineEventType;
  description: string;
  createdBy?: Id;
  createdByName?: string | null;
  status?: string | null;
  previousStatus?: string | null;
  createdAt: string;
}

export type TimelineEventType = 
  | 'CREATED'
  | 'STATUS_CHANGED'
  | 'NOTE_ADDED'
  | 'INTERVIEW_SCHEDULED'
  | 'VISIT_SCHEDULED'
  | 'CONTRACT_SIGNED';

// ============================================
// Adopción Efectiva
// ============================================

export interface Adoption {
  id: Id;
  applicationId: Id;                // UNIQUE
  adoptedAt: string;
  contractUrl?: string | null;
  createdAt?: string;
}

// ============================================
// DTOs para Requests
// ============================================

export interface SubmitFormRequest {
  adopterProfileId?: Id;            // Opcional si no existe
  shelterId: Id;
  animalId?: Id;                    // Opcional
  answers: Record<string, any>;
}

export interface CreateApplicationRequest {
  adopterProfileId: Id;
  animalId: Id;
  shelterId: Id;
  notes?: string;
}

export interface UpdateApplicationStatusRequest {
  status: ApplicationStatus;
}

export interface CreateAdoptionRequest {
  applicationId: Id;
  contractUrl?: string;
}

// ============================================
// Helpers para UI
// ============================================

export interface ApplicationWithDetails extends AdoptionApplication {
  animal?: {
    id: Id;
    name: string;
    photo?: string;
    species_label?: string;
    size_label?: string;
    age?: string;
  };
  shelter?: {
    id: Id;
    name: string;
  };
}

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  SUBMITTED: 'Enviada',
  IN_REVIEW: 'En revisión',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};

export const APPLICATION_STATUS_VARIANTS: Record<ApplicationStatus, 'default' | 'secondary' | 'success' | 'destructive' | 'outline'> = {
  SUBMITTED: 'default',
  IN_REVIEW: 'secondary',
  APPROVED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'outline',
};
